<script>
/**
 * WhatsAppTemplateForm.vue
 *
 * Template creation form with live preview.
 * Layout: form on left (60%), preview on right (40%).
 *
 * Emits:
 *   submit  — payload object (see buildPayload)
 *   cancel  — user cancelled
 */
import WhatsAppTemplatePreview from './WhatsAppTemplatePreview.vue';

export default {
  name: 'WhatsAppTemplateForm',

  components: { WhatsAppTemplatePreview },

  props: {
    saving: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['submit', 'cancel'],

  data() {
    return {
      // Basic fields
      name: '',
      category: 'MARKETING',
      language: 'pt_BR',

      // Header
      headerType: 'none',
      headerText: '',
      headerMediaFile: null,
      headerMediaPreview: '',
      mediaError: '',

      // Body
      body: '',

      // Footer
      footer: '',

      // Buttons
      buttons: [],

      // Dark mode detection
      isDark: false,
    };
  },

  computed: {
    isValid() {
      return this.name.length >= 3 && this.body.length >= 10 && this.mediaOk;
    },

    // Header de mídia exige arquivo enviado.
    isMediaHeader() {
      return ['image', 'video', 'document'].includes(this.headerType);
    },

    mediaOk() {
      return !this.isMediaHeader || !!this.headerMediaFile;
    },

    // accept + limite por tipo de header (limites da Meta p/ template).
    mediaConfig() {
      if (this.headerType === 'image') {
        return { accept: 'image/png,image/jpeg', maxMb: 5, label: 'PNG ou JPG, até 5 MB' };
      }
      if (this.headerType === 'video') {
        return { accept: 'video/mp4', maxMb: 16, label: 'MP4, até 16 MB' };
      }
      if (this.headerType === 'document') {
        return { accept: 'application/pdf', maxMb: 100, label: 'PDF, até 100 MB' };
      }
      return { accept: '', maxMb: 0, label: '' };
    },

    nameError() {
      if (!this.name) return '';
      if (!/^[a-z0-9_]+$/.test(this.name)) {
        return 'Apenas letras minusculas (a-z), numeros (0-9) e sublinhado (_)';
      }
      if (this.name.length < 3) {
        return 'Minimo 3 caracteres';
      }
      return '';
    },

    footerError() {
      if (this.footer.length > 60) {
        return 'Maximo 60 caracteres';
      }
      return '';
    },

    // Props forwarded to the preview component
    previewHeaderType() {
      return this.headerType === 'none' ? 'none' : this.headerType;
    },

    previewHeaderContent() {
      if (this.headerType === 'text') return this.headerText;
      if (this.isMediaHeader) return this.headerMediaPreview;
      return '';
    },

    previewButtons() {
      return this.buttons.map(btn => ({
        type: btn.type,
        text: btn.text,
      }));
    },

    // Sample variable values for live preview
    previewVariables() {
      return {
        1: 'Joao',
        2: '#12345',
        3: 'maria@email.com',
        4: '11 99999-0000',
        5: 'Empresa XYZ',
      };
    },

    // Current timestamp for preview
    previewTimestamp() {
      const now = new Date();
      return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    },
  },

  watch: {
    // Trocar o tipo de header descarta o arquivo escolhido (evita mídia órfã).
    headerType() {
      this.clearMedia();
    },
  },

  mounted() {
    // Dark mode = classe `dark` no <body> (themeHelper.setColorTheme).
    this.isDark = document.body.classList.contains('dark');
  },

  beforeUnmount() {
    if (this.headerMediaPreview) URL.revokeObjectURL(this.headerMediaPreview);
  },

  methods: {
    handleNameInput(e) {
      // Enforce snake_case as user types: strip disallowed chars
      this.name = e.target.value
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, '');
    },

    insertVariable() {
      // Count existing placeholders to determine next index
      const matches = this.body.match(/\{\{(\d+)\}\}/g) || [];
      const indices = matches.map(m => parseInt(m.replace(/\D/g, ''), 10));
      const next = indices.length > 0 ? Math.max(...indices) + 1 : 1;
      this.body += `{{${next}}}`;
    },

    // ── Mídia do header (upload de arquivo, sem URL) ──
    pickFile() {
      this.$refs.mediaInput?.click();
    },

    onFileChange(e) {
      const file = e.target?.files?.[0];
      if (!file) return;
      this.mediaError = '';
      const { maxMb } = this.mediaConfig;
      if (maxMb && file.size > maxMb * 1024 * 1024) {
        this.mediaError = `Arquivo muito grande (máx. ${maxMb} MB).`;
        e.target.value = '';
        return;
      }
      this.clearMedia();
      this.headerMediaFile = file;
      this.headerMediaPreview = URL.createObjectURL(file);
    },

    clearMedia() {
      if (this.headerMediaPreview) URL.revokeObjectURL(this.headerMediaPreview);
      this.headerMediaFile = null;
      this.headerMediaPreview = '';
      this.mediaError = '';
      if (this.$refs.mediaInput) this.$refs.mediaInput.value = '';
    },

    formatBytes(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },

    addButton() {
      if (this.buttons.length >= 3) return;
      this.buttons.push({ type: 'QUICK_REPLY', text: '', url: '', phone_number: '' });
    },

    removeButton(index) {
      this.buttons.splice(index, 1);
    },

    buildPayload() {
      const payload = {
        name: this.name,
        category: this.category,
        language: this.language,
        body: this.body,
      };

      if (this.headerType !== 'none') {
        payload.header_type = this.headerType;
        if (this.headerType === 'text') {
          payload.header_text = this.headerText;
        }
        // Para mídia, o arquivo vai em separado (multipart); o backend gera o handle.
      }

      if (this.footer.trim()) {
        payload.footer_text = this.footer;
      }

      if (this.buttons.length > 0) {
        payload.buttons = this.buttons.map(btn => {
          const b = { type: btn.type, text: btn.text };
          if (btn.type === 'URL') b.url = btn.url;
          if (btn.type === 'PHONE_NUMBER') b.phone_number = btn.phone_number;
          return b;
        });
      }

      return payload;
    },

    handleSubmit() {
      if (!this.isValid || this.saving) return;
      this.$emit('submit', this.buildPayload(), this.headerMediaFile);
    },

    handleCancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<template>
  <div class="wtf-root">
    <!-- ═══ FORM + PREVIEW SPLIT ═══ -->
    <div class="wtf-layout">

      <!-- ─── LEFT: FORM (60%) ─── -->
      <div class="wtf-form-col">
        <form class="wtf-form" @submit.prevent="handleSubmit">

          <!-- NOME -->
          <div class="wtf-field">
            <label class="wtf-label">
              Nome do template
              <span class="wtf-required">*</span>
            </label>
            <input
              :value="name"
              type="text"
              class="wtf-input"
              :class="{ 'wtf-input--error': nameError }"
              placeholder="ex: boas_vindas_marketing"
              autocomplete="off"
              spellcheck="false"
              @input="handleNameInput"
            />
            <p v-if="nameError" class="wtf-error-msg">{{ nameError }}</p>
            <p v-else class="wtf-hint">
              Apenas letras minusculas, numeros e sublinhado. Min. 3 caracteres.
            </p>
          </div>

          <!-- CATEGORIA + IDIOMA side by side -->
          <div class="wtf-row">
            <div class="wtf-field wtf-field--half">
              <label class="wtf-label">
                Categoria
                <span class="wtf-required">*</span>
              </label>
              <select v-model="category" class="wtf-select">
                <option value="MARKETING">MARKETING</option>
                <option value="UTILITY">UTILITY</option>
                <option value="AUTHENTICATION">AUTHENTICATION</option>
              </select>
            </div>

            <div class="wtf-field wtf-field--half">
              <label class="wtf-label">
                Idioma
                <span class="wtf-required">*</span>
              </label>
              <select v-model="language" class="wtf-select">
                <option value="pt_BR">Portugues (BR)</option>
                <option value="en_US">Ingles (US)</option>
                <option value="es">Espanhol</option>
              </select>
            </div>
          </div>

          <!-- HEADER -->
          <div class="wtf-field">
            <label class="wtf-label">Header <span class="wtf-optional">(opcional)</span></label>
            <select v-model="headerType" class="wtf-select">
              <option value="none">Nenhum</option>
              <option value="text">Texto</option>
              <option value="image">Imagem</option>
              <option value="video">Video</option>
              <option value="document">Documento</option>
            </select>

            <!-- Header text input -->
            <input
              v-if="headerType === 'text'"
              v-model="headerText"
              type="text"
              class="wtf-input wtf-mt"
              placeholder="Texto do header"
              maxlength="60"
            />

            <!-- Header media: upload de arquivo (sem URL) -->
            <div v-if="isMediaHeader" class="wtf-mt">
              <input
                ref="mediaInput"
                type="file"
                class="wtf-file-hidden"
                :accept="mediaConfig.accept"
                @change="onFileChange"
              />

              <!-- Sem arquivo: zona de upload -->
              <button
                v-if="!headerMediaFile"
                type="button"
                class="wtf-upload-zone"
                @click="pickFile"
              >
                <span class="i-ri-upload-cloud-line size-5 wtf-upload-icon" />
                <span class="wtf-upload-text">Escolher arquivo do computador</span>
                <span class="wtf-upload-hint">{{ mediaConfig.label }}</span>
              </button>

              <!-- Com arquivo: chip com nome/tamanho -->
              <div v-else class="wtf-file-chip">
                <span class="i-ri-attachment-line size-4 wtf-file-icon" />
                <span class="wtf-file-name">{{ headerMediaFile.name }}</span>
                <span class="wtf-file-size">{{ formatBytes(headerMediaFile.size) }}</span>
                <button
                  type="button"
                  class="wtf-btn-remove"
                  title="Remover arquivo"
                  @click="clearMedia"
                >
                  <span class="i-ri-close-line size-3.5" />
                </button>
              </div>

              <p v-if="mediaError" class="wtf-error-msg wtf-mt-xs">{{ mediaError }}</p>
            </div>
          </div>

          <!-- BODY -->
          <div class="wtf-field">
            <label class="wtf-label">
              Corpo da mensagem
              <span class="wtf-required">*</span>
            </label>
            <div class="wtf-textarea-wrapper">
              <textarea
                v-model="body"
                class="wtf-textarea"
                rows="5"
                :placeholder="'Ola {{1}}, seu pedido {{2}} foi confirmado!'"
              />
              <div class="wtf-textarea-footer">
                <span class="wtf-char-info">{{ body.length }} chars</span>
                <button
                  type="button"
                  class="wtf-btn-ghost"
                  title="Insere a proxima variavel no corpo"
                  @click="insertVariable"
                >
                  <span class="i-ri-functions size-3.5" />
                  Inserir variavel
                </button>
              </div>
            </div>
            <p v-if="body.length > 0 && body.length < 10" class="wtf-error-msg">
              Minimo 10 caracteres
            </p>
            <p v-else class="wtf-hint">
              Use {{1}}, {{2}} ... para variaveis. Suporta *negrito* e _italico_.
            </p>
          </div>

          <!-- FOOTER -->
          <div class="wtf-field">
            <label class="wtf-label">
              Footer
              <span class="wtf-optional">(opcional, max. 60 chars)</span>
            </label>
            <input
              v-model="footer"
              type="text"
              class="wtf-input"
              :class="{ 'wtf-input--error': footerError }"
              placeholder="ex: Nao responda este numero"
              maxlength="60"
            />
            <p v-if="footerError" class="wtf-error-msg">{{ footerError }}</p>
            <p v-else-if="footer.length > 0" class="wtf-hint">{{ footer.length }}/60</p>
          </div>

          <!-- BUTTONS -->
          <div class="wtf-field">
            <div class="wtf-section-header">
              <label class="wtf-label">
                Botoes
                <span class="wtf-optional">(opcional, max. 3)</span>
              </label>
              <button
                v-if="buttons.length < 3"
                type="button"
                class="wtf-btn-ghost"
                @click="addButton"
              >
                <span class="i-ri-add-line size-3.5" />
                Adicionar botao
              </button>
            </div>

            <div class="wtf-buttons-list">
              <div
                v-for="(btn, idx) in buttons"
                :key="idx"
                class="wtf-button-row"
              >
                <!-- Button type -->
                <select v-model="btn.type" class="wtf-select wtf-select--sm">
                  <option value="QUICK_REPLY">Resposta rapida</option>
                  <option value="URL">URL</option>
                  <option value="PHONE_NUMBER">Telefone</option>
                </select>

                <!-- Button text -->
                <input
                  v-model="btn.text"
                  type="text"
                  class="wtf-input wtf-input--sm wtf-flex-1"
                  placeholder="Texto do botao"
                  maxlength="25"
                />

                <!-- URL field -->
                <input
                  v-if="btn.type === 'URL'"
                  v-model="btn.url"
                  type="url"
                  class="wtf-input wtf-input--sm wtf-flex-1"
                  placeholder="https://..."
                />

                <!-- Phone field -->
                <input
                  v-if="btn.type === 'PHONE_NUMBER'"
                  v-model="btn.phone_number"
                  type="tel"
                  class="wtf-input wtf-input--sm wtf-flex-1"
                  placeholder="+55 11 99999-9999"
                />

                <!-- Remove button -->
                <button
                  type="button"
                  class="wtf-btn-remove"
                  title="Remover botao"
                  @click="removeButton(idx)"
                >
                  <span class="i-ri-close-line size-3.5" />
                </button>
              </div>
            </div>
          </div>

          <!-- ACTIONS -->
          <div class="wtf-actions">
            <button
              type="button"
              class="wtf-btn wtf-btn--secondary"
              @click="handleCancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              class="wtf-btn wtf-btn--primary"
              :disabled="saving || !isValid"
            >
              <span v-if="saving" class="i-ri-loader-4-line size-4 wtf-spin" />
              <span v-else class="i-ri-send-plane-line size-4" />
              {{ saving ? 'Enviando...' : 'Enviar para aprovacao' }}
            </button>
          </div>

        </form>
      </div>

      <!-- ─── RIGHT: PREVIEW (40%) ─── -->
      <div class="wtf-preview-col">
        <div class="wtf-preview-sticky">
          <div class="wtf-preview-header">
            <span class="i-ri-eye-line size-4 wtf-preview-icon" />
            <span class="wtf-preview-title">Previa ao vivo</span>
          </div>
          <WhatsAppTemplatePreview
            :header-type="previewHeaderType"
            :header-content="previewHeaderContent"
            :header-filename="headerMediaFile ? headerMediaFile.name : ''"
            :body="body"
            :footer="footer"
            :buttons="previewButtons"
            :variables="previewVariables"
            :timestamp="previewTimestamp"
            :dark-mode="isDark"
          />
          <p class="wtf-preview-note">
            Variaveis de amostra: {{1}}=Joao, {{2}}=#12345, {{3}}=maria@email.com
          </p>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
/* ── Root ── */
.wtf-root {
  font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif;
  color: var(--color-n-slate-12, #1c1c1e);
  font-size: 0.875rem;
}

/* ── Two-column layout ── */
.wtf-layout {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.wtf-form-col {
  flex: 0 0 60%;
  min-width: 0;
}

.wtf-preview-col {
  flex: 0 0 40%;
  min-width: 0;
}

/* ── Sticky preview ── */
.wtf-preview-sticky {
  position: sticky;
  top: 1rem;
}

.wtf-preview-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
}

.wtf-preview-icon {
  color: var(--color-n-slate-11, #64748b);
}

.wtf-preview-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-n-slate-11, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.wtf-preview-note {
  margin-top: 0.5rem;
  font-size: 0.7rem;
  color: var(--color-n-slate-10, #94a3b8);
  line-height: 1.4;
}

/* ── Form ── */
.wtf-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* ── Field wrappers ── */
.wtf-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.wtf-row {
  display: flex;
  gap: 1rem;
}

.wtf-field--half {
  flex: 1 1 0;
  min-width: 0;
}

.wtf-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* ── Labels ── */
.wtf-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-n-slate-11, #64748b);
}

.wtf-required {
  color: var(--color-n-ruby-9, #e11d48);
  margin-left: 2px;
}

.wtf-optional {
  font-weight: 400;
  font-size: 0.75rem;
  color: var(--color-n-slate-10, #94a3b8);
  margin-left: 4px;
}

/* ── Inputs / Selects ── */
.wtf-input,
.wtf-select {
  width: 100%;
  padding: 0.4375rem 0.625rem;
  font-size: 0.875rem;
  border: 1px solid var(--color-n-slate-6, #cbd5e1);
  border-radius: 0.5rem;
  background: var(--color-n-surface-1, #fff);
  color: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
  box-sizing: border-box;
}

.wtf-input:focus,
.wtf-select:focus {
  border-color: var(--color-n-brand, #6366f1);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}

.wtf-input--error {
  border-color: var(--color-n-ruby-9, #e11d48);
}

.wtf-input--sm,
.wtf-select--sm {
  padding: 0.3125rem 0.5rem;
  font-size: 0.8125rem;
}

.wtf-flex-1 {
  flex: 1 1 0;
  width: auto;
}

.wtf-mt {
  margin-top: 0.5rem;
}

.wtf-mt-xs {
  margin-top: 0.375rem;
}

/* ── Upload de mídia do header ── */
.wtf-file-hidden {
  display: none;
}

.wtf-upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  padding: 1.25rem 1rem;
  border: 1.5px dashed var(--color-n-slate-6, #cbd5e1);
  border-radius: 0.5rem;
  background: var(--color-n-slate-2, #f8fafc);
  color: var(--color-n-slate-11, #64748b);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.wtf-upload-zone:hover {
  border-color: var(--color-n-brand, #6366f1);
  background: var(--color-n-slate-3, #f1f5f9);
}

.wtf-upload-icon {
  color: var(--color-n-brand, #6366f1);
}

.wtf-upload-text {
  font-size: 0.8125rem;
  font-weight: 600;
}

.wtf-upload-hint {
  font-size: 0.7rem;
  color: var(--color-n-slate-10, #94a3b8);
}

.wtf-file-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  border: 1px solid var(--color-n-slate-6, #cbd5e1);
  border-radius: 0.5rem;
  background: var(--color-n-slate-2, #f8fafc);
}

.wtf-file-icon {
  color: var(--color-n-slate-10, #94a3b8);
  flex-shrink: 0;
}

.wtf-file-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wtf-file-size {
  font-size: 0.7rem;
  color: var(--color-n-slate-10, #94a3b8);
  flex-shrink: 0;
}

/* ── Textarea ── */
.wtf-textarea-wrapper {
  border: 1px solid var(--color-n-slate-6, #cbd5e1);
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--color-n-surface-1, #fff);
  transition: border-color 0.15s, box-shadow 0.15s;
}

.wtf-textarea-wrapper:focus-within {
  border-color: var(--color-n-brand, #6366f1);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}

.wtf-textarea {
  width: 100%;
  padding: 0.5rem 0.625rem;
  font-size: 0.875rem;
  background: transparent;
  color: inherit;
  resize: vertical;
  border: none;
  outline: none;
  line-height: 1.5;
  box-sizing: border-box;
  font-family: inherit;
}

.wtf-textarea-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0.5rem;
  background: var(--color-n-slate-2, #f8fafc);
  border-top: 1px solid var(--color-n-slate-4, #e2e8f0);
}

.wtf-char-info {
  font-size: 0.7rem;
  color: var(--color-n-slate-10, #94a3b8);
}

/* ── Hint / Error ── */
.wtf-hint {
  font-size: 0.75rem;
  color: var(--color-n-slate-10, #94a3b8);
  margin: 0;
  line-height: 1.4;
}

.wtf-error-msg {
  font-size: 0.75rem;
  color: var(--color-n-ruby-9, #e11d48);
  margin: 0;
  line-height: 1.4;
}

/* ── Ghost button (inline actions) ── */
.wtf-btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-n-brand, #6366f1);
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background 0.15s;
}

.wtf-btn-ghost:hover {
  background: var(--color-n-slate-3, #f1f5f9);
}

/* ── Button rows ── */
.wtf-buttons-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.wtf-button-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--color-n-slate-2, #f8fafc);
  border: 1px solid var(--color-n-slate-4, #e2e8f0);
  border-radius: 0.5rem;
  flex-wrap: wrap;
}

.wtf-btn-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: var(--color-n-ruby-9, #e11d48);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.wtf-btn-remove:hover {
  background: rgba(225, 29, 72, 0.1);
}

/* ── Action buttons ── */
.wtf-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--color-n-slate-4, #e2e8f0);
}

.wtf-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}

.wtf-btn--primary {
  background: var(--color-n-brand, #6366f1);
  color: #fff;
}

.wtf-btn--primary:hover:not(:disabled) {
  background: var(--color-n-brand-dark, #4f46e5);
}

.wtf-btn--primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.wtf-btn--secondary {
  background: var(--color-n-slate-3, #f1f5f9);
  color: var(--color-n-slate-11, #64748b);
}

.wtf-btn--secondary:hover {
  background: var(--color-n-slate-4, #e2e8f0);
}

/* ── Spinner ── */
@keyframes wtf-spin {
  to { transform: rotate(360deg); }
}

.wtf-spin {
  animation: wtf-spin 0.8s linear infinite;
}

/* ── Dark mode overrides via data-theme ── */
:global(.dark) .wtf-root {
  color: var(--color-n-slate-12, #e2e8f0);
}

:global(.dark) .wtf-input,
:global(.dark) .wtf-select,
:global(.dark) .wtf-textarea {
  background: var(--color-n-slate-3, #1e293b);
  border-color: var(--color-n-slate-6, #334155);
  color: var(--color-n-slate-12, #e2e8f0);
}

:global(.dark) .wtf-textarea-wrapper {
  background: var(--color-n-slate-3, #1e293b);
  border-color: var(--color-n-slate-6, #334155);
}

:global(.dark) .wtf-textarea-footer {
  background: var(--color-n-slate-2, #0f172a);
  border-color: var(--color-n-slate-5, #1e293b);
}

:global(.dark) .wtf-button-row {
  background: var(--color-n-slate-2, #0f172a);
  border-color: var(--color-n-slate-5, #1e293b);
}

:global(.dark) .wtf-btn-ghost:hover {
  background: var(--color-n-slate-4, #1e293b);
}

:global(.dark) .wtf-btn--secondary {
  background: var(--color-n-slate-4, #1e293b);
  color: var(--color-n-slate-11, #94a3b8);
}

:global(.dark) .wtf-btn--secondary:hover {
  background: var(--color-n-slate-5, #334155);
}

:global(.dark) .wtf-actions {
  border-color: var(--color-n-slate-5, #1e293b);
}

:global(.dark) .wtf-upload-zone,
:global(.dark) .wtf-file-chip {
  background: var(--color-n-slate-2, #0f172a);
  border-color: var(--color-n-slate-6, #334155);
  color: var(--color-n-slate-11, #94a3b8);
}

:global(.dark) .wtf-upload-zone:hover {
  background: var(--color-n-slate-3, #1e293b);
  border-color: var(--color-n-brand, #6366f1);
}
</style>
